import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import SEO from "@/components/SEO";

const PrivacyPolicy = () => (
  <div className="min-h-screen bg-background">
    <SEO
      title="Privacy Policy | HeirWise"
      description="How HeirWise collects, uses, shares, retains, and protects information through heirwise.app."
      path="/privacy-policy"
    />
    <div className="container mx-auto max-w-3xl px-6 py-12 sm:py-16">
      <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-8">
        <ArrowLeft className="h-4 w-4" /> Back to Home
      </Link>

      <h1 className="text-3xl sm:text-4xl font-serif font-bold text-foreground mb-2">Privacy Policy</h1>
      <p className="text-sm text-muted-foreground mb-10">Last Updated: September 23, 2026</p>

      <div className="prose prose-sm sm:prose-base max-w-none text-foreground/90 [&_h2]:font-serif [&_h2]:text-foreground [&_h2]:text-xl [&_h2]:sm:text-2xl [&_h2]:mt-10 [&_h2]:mb-4 [&_p]:leading-relaxed [&_p]:mb-4 [&_ul]:mb-4 [&_li]:mb-1.5">

        <p>
          HeirWise LLC ("Company," "we," "us," or "our") respects your privacy. This Privacy Policy explains how we collect, use, disclose, retain, and protect information when you visit heirwise.app, create or join a household account, use the HeirWise platform, purchase a subscription, contact us, or otherwise interact with our services. Service emails may be sent from HeirWise addresses using the heirwise.app domain.
        </p>

        <h2>1. Information We Collect</h2>
        <p>We may collect the following categories of information:</p>

        <p><strong>Information you provide directly to us.</strong><br />
          This may include your name or display name, email address, username, authentication information, account settings, profile information, household role, age range, family invitation information, and other information you choose to provide. Passwords and authentication credentials are handled through our managed authentication service rather than displayed to HeirWise staff.
        </p>

        <p><strong>Household and learner information.</strong><br />
          For parent-managed households, we collect information needed to connect parent or guardian accounts with eligible teen learners, manage plan limits and permissions, and provide household progress views.
        </p>

        <p><strong>Educational and platform activity information.</strong><br />
          We collect lesson and module activity, assessment questions and responses, scores, progress and completion records, learning evidence, competency state, decision-practice activity, simulations, rewards, and other product interactions. This information supports adaptive learning, learner progress, and evidence-safe parent reporting.
        </p>

        <p><strong>Transaction and billing information.</strong><br />
          Stripe processes checkout, payment credentials, recurring charges, invoices, subscription status, and related billing events. HeirWise receives transaction and subscription records needed to provide and manage access, but does not store full payment card numbers.
        </p>

        <p><strong>Usage and device information.</strong><br />
          We and our service providers may automatically collect information such as IP address, browser type, device type, operating system, referral URLs, pages viewed, session activity, approximate location derived from IP address, and similar usage information.
        </p>

        <p><strong>Communications and support information.</strong><br />
          We may keep records of emails, support requests, feedback, survey responses, attachments, and other communications you send to us.
        </p>

        <p><strong>Cookies and similar technologies.</strong><br />
          We use cookies, browser local storage, and similar session technologies to keep users signed in, remember appropriate preferences and referral or invitation state, support analytics, and protect platform security.
        </p>

        <h2>2. How We Use Information</h2>
        <p>We may use personal information to:</p>
        <ul>
          <li>provide, operate, maintain, and improve the website, platform, and services;</li>
          <li>create and manage accounts and authenticate users;</li>
          <li>process subscriptions, payments, and related transactions;</li>
          <li>deliver platform features, educational content, and account functionality;</li>
          <li>track learning progress and personalize the user experience;</li>
          <li>create adaptive learning decisions, assessments, instructional media, and parent-facing progress summaries;</li>
          <li>communicate with you about your account, transactions, updates, support requests, and service-related notices;</li>
          <li>monitor performance, analyze usage, troubleshoot issues, and improve the platform;</li>
          <li>detect, investigate, and prevent fraud, abuse, security incidents, or misuse; and</li>
          <li>comply with legal obligations, enforce our terms, and protect our business, users, and rights.</li>
        </ul>

        <h2>3. Cookies and Similar Technologies</h2>
        <p>
          We may use cookies and similar technologies to support authentication, maintain sessions, remember preferences, improve platform functionality, analyze traffic and usage, and help protect the platform.
        </p>
        <p>
          You may be able to manage cookies through your browser settings, but disabling cookies may affect platform functionality.
        </p>
        <p>
          Our managed hosting, authentication, analytics, and payment providers may also use cookies or similar technologies in connection with the services they provide to us.
        </p>

        <h2>4. How We Share Information</h2>
        <p>We may share information:</p>
        <ul>
          <li>with service providers that help us operate the platform, including Lovable-managed hosting, authentication, database, storage, and functions; Stripe for billing and payments; Resend for transactional email; and analytics and advertising-measurement providers such as PostHog, Google Analytics, and Meta. HeirWise applies data-minimization controls to product analytics and does not intentionally send learner names, email addresses, household details, sensitive assessment responses, or competency diagnoses to advertising platforms;</li>
          <li>with selected artificial-intelligence, research, voice, image, and video providers when needed to generate, validate, or deliver educational features. These providers may include OpenAI, Google Gemini, Perplexity, ElevenLabs, fal.ai, and VEED. We limit the information sent for these purposes and do not intentionally include learner names, parent names, email addresses, or household identifiers when educational context is sufficient;</li>
          <li>with professional advisers, such as legal, accounting, or business advisers, where reasonably necessary;</li>
          <li>in connection with an actual or proposed merger, acquisition, financing, sale of assets, reorganization, or similar business transaction;</li>
          <li>when required by law, legal process, or governmental request; or</li>
          <li>when reasonably necessary to protect rights, safety, property, or the security of our business, users, or others.</li>
        </ul>
        <p>
          We do not sell personal information in exchange for money. If we engage in activities that are legally defined as "sharing" or targeted advertising under applicable law, we will provide any notices and rights required by law.
        </p>

        <h2>5. Data Retention</h2>
        <p>
          We retain personal information for as long as reasonably necessary to provide the Services, maintain accounts and learning records, meet security and financial recordkeeping needs, resolve disputes, enforce agreements, and comply with legal obligations. Retention differs by record type; billing, security, audit, suppression, and de-identified or aggregated records may be kept after an account or learner profile is deleted when reasonably necessary.
        </p>
        <p>
          Retention periods may vary depending on the type of information, the purpose for which it was collected, account status, and legal or operational requirements.
        </p>

        <h2>6. Data Security</h2>
        <p>
          We use reasonable administrative, technical, and organizational safeguards designed to protect personal information. However, no method of transmission over the internet or electronic storage is completely secure, and we cannot guarantee absolute security.
        </p>

        <h2>7. Your Choices and Rights</h2>
        <p>
          You may be able to update certain account information by logging into your account.
        </p>
        <p>
          You may unsubscribe from non-essential marketing emails using the unsubscribe link in those emails, where applicable. We may still send transactional, account, billing, security, and service-related communications.
        </p>
        <p>
          Depending on where you live and the laws that apply, you may have rights such as the right to request access, correction, deletion, export, or information about how your personal information is processed. Parent or guardian account holders can use Family Privacy Controls to export or delete an eligible teen learner profile. Deletion removes learner-identifying records while limited audit records and anonymized aggregate learning statistics may be retained. We handle qualifying requests in accordance with applicable law.
        </p>
        <p>
          To make a privacy-related request, contact us at{" "}
          <a href="mailto:founder@steenventures.com" className="text-primary underline underline-offset-2">founder@steenventures.com</a>.
        </p>

        <h2>8. Third-Party Links and Services</h2>
        <p>
          The website or platform may contain links to third-party websites, tools, media, or services. We are not responsible for the privacy practices, content, or policies of third parties.
        </p>

        <h2>9. Children's Privacy</h2>
        <p>
          HeirWise is intended only for users who are 13 years of age or older. We do not allow children under 13 to create accounts, and we do not knowingly collect personal information from children under 13 through the platform. If we learn that we have collected personal information from a child under 13 without appropriate authorization or legal basis, we will take steps to delete that information. Parents or guardians who believe a child under 13 may have provided personal information to us may contact us at{" "}
          <a href="mailto:founder@steenventures.com" className="text-primary underline underline-offset-2">founder@steenventures.com</a>.
        </p>

        <h2>10. Changes to This Policy</h2>
        <p>
          We may update this Privacy Policy from time to time. We will post the updated version on this page and revise the "Last Updated" date. Material changes may also be communicated by additional notice where appropriate.
        </p>

        <h2>11. Contact Us</h2>
        <p>
          If you have questions about this Privacy Policy or our data practices, contact us at{" "}
          <a href="mailto:founder@steenventures.com" className="text-primary underline underline-offset-2">founder@steenventures.com</a>.
        </p>
      </div>
    </div>
  </div>
);

export default PrivacyPolicy;
